import { useMemo } from 'react';
import { useQuery, useQueryClient } from 'react-query';

// Long enough that a remount reuses the job just made, short enough that a revisit asks again.
const START_STALE_MS = 5000;
// A job still unfinished after this is treated as lost, so it cannot hold its slot for good.
const LOST_JOB_MS = 2 * 60 * 1000;

// The job each statistics query is waiting on, per QueryClient so tests never share one.
const runningJobsByClient = new WeakMap();
const runningJobsOf = queryClient => {
  if (!runningJobsByClient.has(queryClient)) runningJobsByClient.set(queryClient, new Map());
  return runningJobsByClient.get(queryClient);
};

const isActive = status => status === 'queued' || status === 'running';

/**
 * Statistics are computed as a server job: POST makes one, GET polls it until it completes.
 * Every consumer of the same statistics shares the job, its polling and its result. A refresh asked
 * for while a job is running does not start a second one; it schedules one follow-up instead.
 */
export function useStatisticsJob({
  queryKey,
  periodId,
  params = {},
  type,
  queryParams = {},
  createJob,
  getJob
}) {
  const queryClient = useQueryClient();
  const paramsKey = useMemo(() => JSON.stringify(params || {}), [params]);
  const startKey = useMemo(
    () => [...queryKey, 'statistics-job', type, paramsKey],
    [queryKey, type, paramsKey]
  );
  const resultKey = useMemo(
    () => [...queryKey, 'statistics-result', type, paramsKey],
    [queryKey, type, paramsKey]
  );
  const slot = JSON.stringify(startKey);
  const runningJobs = runningJobsOf(queryClient);

  const {
    onSuccess,
    onError,
    refetchInterval,
    statisticsJobPollInterval,
    staleTime = START_STALE_MS,
    ...restQueryParams
  } = queryParams;
  const enabled = queryParams.enabled !== false && !!periodId;

  // Filled by the job flow below, never fetched; disabled so an invalidation cannot call it.
  const { data: result } = useQuery(resultKey, () => queryClient.getQueryData(resultKey) ?? null, {
    enabled: false,
    staleTime: Infinity,
    cacheTime: 10 * 60 * 1000
  });

  const runningJob = () => {
    const job = runningJobs.get(slot);
    if (job && Date.now() - job.startedAt > LOST_JOB_MS) runningJobs.delete(slot);
    return runningJobs.get(slot);
  };

  const startQuery = useQuery(
    startKey,
    async () => {
      const running = runningJob();
      if (running) {
        running.followUp = true;
        return { job_id: running.jobId, status: 'running' };
      }
      const job = await createJob({ type, period_id: periodId, params });
      if (job?.job_id) {
        runningJobs.set(slot, { jobId: job.job_id, followUp: false, startedAt: Date.now() });
      }
      return job;
    },
    {
      ...restQueryParams,
      staleTime,
      enabled,
      refetchInterval: data => {
        if (runningJob()) return false;
        if (typeof refetchInterval === 'function') return refetchInterval(data);
        return refetchInterval || false;
      },
      onError
    }
  );

  // Finishes the slot once; whoever finishes it starts the follow-up a refresh asked for meanwhile.
  const settle = jobId => {
    const running = runningJobs.get(slot);
    if (running?.jobId !== jobId) return;
    runningJobs.delete(slot);
    if (running.followUp) queryClient.invalidateQueries(startKey, { exact: true });
  };

  const jobId = startQuery.data?.job_id;
  const jobQuery = useQuery(['STATISTICS_JOB_STATUS', jobId], () => getJob(jobId), {
    ...restQueryParams,
    // Polled by the interval while the job runs; a finished job never changes, so never refetched.
    staleTime: Infinity,
    enabled: enabled && !!jobId,
    refetchInterval: data => {
      if (data && !isActive(data.status)) return false;
      if (typeof statisticsJobPollInterval === 'function')
        return statisticsJobPollInterval(data) || 1000;
      if (typeof statisticsJobPollInterval === 'number') return statisticsJobPollInterval;
      if (typeof refetchInterval === 'function') return refetchInterval(data) || 1000;
      if (typeof refetchInterval === 'number') return refetchInterval;
      return 1000;
    },
    onSuccess: data => {
      if (data?.status === 'completed') {
        queryClient.setQueryData(resultKey, data.result);
        onSuccess?.(data.result);
      }
      if (data?.status === 'failed') onError?.(new Error(data.error || 'Statistics job failed'));
      if (data && !isActive(data.status)) settle(jobId);
    },
    onError: error => {
      settle(jobId);
      onError?.(error);
    }
  });

  const jobStatus = jobQuery.data?.status;
  const jobError = useMemo(
    () =>
      jobStatus === 'failed' ? new Error(jobQuery.data?.error || 'Statistics job failed') : null,
    [jobStatus, jobQuery.data?.error]
  );
  const isJobRunning = isActive(jobStatus);

  return {
    ...jobQuery,
    data: result,
    error: jobQuery.error || startQuery.error || jobError,
    isError: startQuery.isError || jobQuery.isError || !!jobError,
    isSuccess: result !== undefined && !jobError,
    isLoading: result === undefined && (startQuery.isLoading || jobQuery.isLoading || isJobRunning),
    isFetching: startQuery.isFetching || jobQuery.isFetching || isJobRunning,
    refetch: startQuery.refetch
  };
}
